import React, { useState, useEffect } from 'react';
import { X, Shield, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const promoContent = {
  en: {
    teaser: 'Book by May 1st — Free Cancellation Insurance (worth £150)',
    title: 'Book with Total Peace of Mind',
    description: 'We want you to feel confident booking your child\'s place. That\'s why we\'re offering free cancellation insurance (worth £150) on all bookings made before May 1st.',
    highlight: 'If something unexpected comes up, you\'re fully covered — no questions asked.',
    detail: '50% deposit secures your spot. Cancel for any reason and get your full deposit back.',
    deadline: 'Offer ends May 1st, 2026',
    cta: 'Reserve Your Spot',
    close: 'Close',
    days: 'days',
    hours: 'hrs',
    minutes: 'min',
    seconds: 'sec',
    timeLeft: 'Time left'
  },
  es: {
    teaser: 'Reserva antes del 1 de mayo — Seguro de cancelación gratis (valor £150)',
    title: 'Reserva con Total Tranquilidad',
    description: 'Queremos que te sientas seguro al reservar la plaza de tu hijo. Por eso ofrecemos seguro de cancelación gratuito (valor £150) en todas las reservas realizadas antes del 1 de mayo.',
    highlight: 'Si surge algo inesperado, estarás completamente cubierto — sin preguntas.',
    detail: 'Un depósito del 50% asegura tu plaza. Cancela por cualquier motivo y recupera tu depósito íntegro.',
    deadline: 'La oferta finaliza el 1 de mayo de 2026',
    cta: 'Reserva Tu Plaza',
    close: 'Cerrar',
    days: 'días',
    hours: 'hrs',
    minutes: 'min',
    seconds: 'seg',
    timeLeft: 'Tiempo restante'
  },
  fr: {
    teaser: 'Réservez avant le 1er mai — Assurance annulation gratuite (valeur £150)',
    title: 'Réservez en Toute Sérénité',
    description: 'Nous voulons que vous réserviez en toute confiance. C\'est pourquoi nous offrons une assurance annulation gratuite (valeur £150) sur toutes les réservations effectuées avant le 1er mai.',
    highlight: 'Si un imprévu survient, vous êtes entièrement couvert — sans condition.',
    detail: 'Un acompte de 50% réserve votre place. Annulez pour n\'importe quelle raison et récupérez la totalité de votre acompte.',
    deadline: 'L\'offre se termine le 1er mai 2026',
    cta: 'Réservez Votre Place',
    close: 'Fermer',
    days: 'jours',
    hours: 'hrs',
    minutes: 'min',
    seconds: 'sec',
    timeLeft: 'Temps restant'
  }
};

const DEADLINE = new Date('2026-05-01T23:59:59');

const CountdownTimer = ({ content }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = DEADLINE - now;
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  const units = [
    { value: timeLeft.days, label: content.days },
    { value: timeLeft.hours, label: content.hours },
    { value: timeLeft.minutes, label: content.minutes },
    { value: timeLeft.seconds, label: content.seconds }
  ];

  return (
    <div className="flex items-center justify-center gap-3 my-4" data-testid="countdown-timer">
      {units.map((unit, i) => (
        <div key={i} className="flex flex-col items-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 min-w-[52px]">
            <span className="text-2xl md:text-3xl font-black text-white tabular-nums">
              {String(unit.value).padStart(2, '0')}
            </span>
          </div>
          <span className="text-[10px] uppercase tracking-wider text-gray-400 mt-1">{unit.label}</span>
        </div>
      ))}
    </div>
  );
};

const PromoTopbar = () => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    return sessionStorage.getItem('promoDismissed') === 'true';
  });

  const content = promoContent[language] || promoContent.en;

  const getLocalizedPath = (path) => {
    if (language === 'es') return `/es${path}`;
    if (language === 'fr') return `/fr${path}`;
    return path;
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsDismissed(true);
    sessionStorage.setItem('promoDismissed', 'true');
  };

  if (isDismissed) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-emerald-700 via-green-700 to-emerald-800 text-white" data-testid="promo-container">
      {/* Collapsed Teaser Bar */}
      <div
        className="cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="promo-topbar"
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 pr-10">
          <Shield className="h-4 w-4 text-green-300" />
          <span className="text-sm font-bold tracking-wide">{content.teaser}</span>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* Expanded Offer Details */}
      {isExpanded && (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border-t border-green-600/40">
          <div className="max-w-4xl mx-auto px-4 py-8 text-center">
            <div className="inline-flex items-center gap-2 bg-green-600/20 border border-green-500/30 rounded-full px-4 py-1 mb-4">
              <Shield className="h-4 w-4 text-green-400" />
              <span className="text-green-400 text-xs font-bold uppercase tracking-wider">{content.deadline}</span>
            </div>

            <h3 className="text-2xl md:text-3xl font-black mb-3 text-white">
              {content.title}
            </h3>
            <p className="text-base text-gray-300 mb-2 max-w-2xl mx-auto">
              {content.description}
            </p>
            <p className="text-sm text-green-400 font-semibold mb-1">
              {content.highlight}
            </p>
            <p className="text-sm text-gray-400 mb-4">
              {content.detail}
            </p>

            <CountdownTimer content={content} />

            <div className="flex items-center justify-center gap-4 mt-4">
              <Link
                to={getLocalizedPath('/signup')}
                className="inline-block bg-green-600 hover:bg-green-500 text-white font-bold px-8 py-3 rounded-full transition-all transform hover:scale-105 shadow-lg"
                data-testid="promo-cta-btn"
              >
                {content.cta}
              </Link>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-white text-sm underline"
                data-testid="promo-close-btn"
              >
                {content.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close button on collapsed bar */}
      <button
        onClick={handleDismiss}
        className="absolute right-3 top-2.5 p-1 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Close promotion"
        data-testid="promo-dismiss-btn"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PromoTopbar;
